import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { SUPPORT_EMAIL } from 'src/app/constants/emailAndLinks';
import { IMAGES, STACK_IMAGES } from 'src/app/constants/imagePath';
import { ROUTES } from 'src/app/constants/Routes';
import { AnalyticsService } from 'src/app/service/analytics.service';

declare const gsap: any;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit {
  Routes = ROUTES;
  Images = IMAGES;
  SupportEmail = SUPPORT_EMAIL;
  StackImages = STACK_IMAGES;
  differentiators = [
    {
      icon: 'bx-code-block',
      title: 'Unified SDK',
      desc: 'A single, integrated SDK that simplifies development and accelerates deployment across multiple devices and platforms, reducing complexity and time to market.',
      tags: ['Multi-Platform', 'Faster Launches'],
      route: ROUTES.products_unitedsdk,
    },
    {
      icon: 'bx-trophy',
      title: 'Miles (Rewards Engine)',
      desc: 'Drive engagement with gamification and rewards by incentivizing safe driving behavior and user actions through a flexible points-based ecosystem.',
      tags: ['Gamification', 'Retention'],
      route: ROUTES.products_connectiq_miles,
    },
    {
      icon: 'bx-message-dots',
      title: 'iQ360',
      desc: 'Deliver intelligent, real-time communication based on user behavior, vehicle data, and contextual triggers for relevant and timely engagement.',
      tags: ['Real-Time', 'Triggered Journeys'],
      route: ROUTES.products_connectiq_context_based_messaging,
    },
    {
      icon: 'bx-transfer',
      title: 'FleetIQ',
      desc: 'Advanced fleet intelligence and management capabilities provide visibility, control, and optimization for fleet operations at scale.',
      tags: ['Operational Control', 'Optimization'],
      route: ROUTES.solutions_business_fleet,
    },
    {
      icon: 'bx-store-alt',
      title: 'VStore',
      desc: 'A digital marketplace for connected services and features, enabling seamless discovery, purchase, and activation of value-added offerings.',
      tags: ['Commerce', 'Activation'],
      route: ROUTES.products_connectiq_vstore,
    },
    {
      icon: 'bx-grid-alt',
      title: 'In-Vehicle App Store',
      desc: 'Bring a rich app ecosystem directly into the vehicle to enhance user experience with on-demand services, entertainment, and utilities.',
      tags: ['App Ecosystem', 'On-Demand'],
      route: ROUTES.products_connectiq_vehicle_app_store,
    },
    {
      icon: 'bx-user-check',
      title: 'Customer Adoption Program',
      desc: 'Designed to maximize user onboarding and engagement through targeted campaigns, incentives, and continuous lifecycle communication.',
      tags: ['Onboarding', 'Lifecycle Growth'],
      route: ROUTES.products_connectiq_cap,
    },
  ];
  @ViewChild('testimonialContainer', { static: false }) testimonialContainer: ElementRef | undefined;
  @ViewChild('industrySolutionsContainer', { static: false }) industrySolutionsContainer: ElementRef | undefined;
  @ViewChild('useCaseContainer', { static: false }) useCaseContainer: ElementRef | undefined;
  @ViewChild('platformContainer', { static: false }) platformContainer: ElementRef | undefined;
  @ViewChild('stackContainer', { static: false }) stackContainer: ElementRef | undefined;
  @ViewChild('testimonialsContainer', { static: false }) testimonialsContainer: ElementRef | undefined;
  isButtonVisible = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isButtonVisible = window.scrollY > 650;
  }

  constructor(private analytics: AnalyticsService) { }

  ngOnInit() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.initGSAPAnimations();
  }

  private initGSAPAnimations() {
    // Wait for GSAP to load
    setTimeout(() => {
      if (typeof gsap !== 'undefined') {
        // Animate hero text
        if (document.querySelector('.neon-text')) {
          gsap.from('.neon-text', {
            y: 20,
            opacity: 0,
            duration: 1,
            ease: 'power2.out'
          });
        }

        // Animate cards on scroll
        if (typeof gsap.utils !== 'undefined') {
          const cards = gsap.utils.toArray('.card-hover');
          if (cards.length > 0) {
            cards.forEach((card: any) => {
              gsap.from(card, {
                scrollTrigger: {
                  trigger: card,
                  start: 'top 85%',
                },
                y: 30,
                opacity: 0,
                duration: 0.8,
                ease: 'power2.out'
              });
            });
          }
        }
      }
    }, 500);
  }

  ctaEvent(event_name: string, event_label: string, userId?: string) {
    this.analytics.trackUserInteraction(event_name, event_label, userId || 'guest');
  }

  scrollLeft() {
    if (this.testimonialContainer) {
      this.testimonialContainer.nativeElement.scrollBy({
        left: -this.testimonialContainer.nativeElement.offsetWidth,
        behavior: 'smooth'
      });
    }
  }

  scrollRight() {
    if (this.testimonialContainer) {
      this.testimonialContainer.nativeElement.scrollBy({
        left: this.testimonialContainer.nativeElement.offsetWidth,
        behavior: 'smooth'
      });
    } 
  }

  scrollIndustrySolutionsLeft() {
    if (this.industrySolutionsContainer) {
      this.industrySolutionsContainer.nativeElement.scrollBy({
        left: -336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollIndustrySolutionsRight() {
    if (this.industrySolutionsContainer) {
      this.industrySolutionsContainer.nativeElement.scrollBy({
        left: 336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollUseCaseLeft() {
    if (this.useCaseContainer) {
      this.useCaseContainer.nativeElement.scrollBy({
        left: -336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollUseCaseRight() {
    if (this.useCaseContainer) {
      this.useCaseContainer.nativeElement.scrollBy({
        left: 336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollPlatformLeft() {
    if (this.platformContainer) {
      this.platformContainer.nativeElement.scrollBy({
        left: -336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollPlatformRight() {
    if (this.platformContainer) {
      this.platformContainer.nativeElement.scrollBy({
        left: 336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollStackLeft() {
    if (this.stackContainer) {
      this.stackContainer.nativeElement.scrollBy({
        left: -280, // Scroll by image width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollStackRight() {
    if (this.stackContainer) {
      this.stackContainer.nativeElement.scrollBy({
        left: 280, // Scroll by image width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollTestimonialsLeft() {
    if (this.testimonialsContainer) {
      this.testimonialsContainer.nativeElement.scrollBy({
        left: -336, // Scroll by card width + gap (320px + 16px)
        behavior: 'smooth'
      });
    }
  }

  scrollTestimonialsRight() {
    if (this.testimonialsContainer) {
      this.testimonialsContainer.nativeElement.scrollBy({
        left: 336, // Scroll by card width + gap
        behavior: 'smooth'
      });
    }
  }

  scrollToElement($element:any): void {
    $element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }

}
